import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Fullscreen, FullscreenExit, PlayFill, PeopleFill } from 'react-bootstrap-icons';
import { useLocale } from '../../i18n/LocaleContext';
import { normalizeWheelWinner } from '../prizeWheelShape';
import { momentAt, planSpin } from './wheelSpin';
import { Avatar, ProductThumb } from './WinnerBits';
import { fmtNum, useNow } from './wheelUi';

// The draw as the brand runs it (contract v2) — big enough to livestream:
//   ready     the wheel idles on the server clock; a BIG "Sorsolás indítása"
//             button with the 30 s auto-start countdown sits on the stage
//   drawing   the spin plays from the spin object + the server clock — the
//             same frames the app shows (wheelSpin.js); between rounds the
//             round's winner and products are revealed on the stage
//   finished  the last stop, every winner gold
// "Teljes képernyő" puts the whole stage (wheel + overlays + start button)
// into full screen. One WebGL canvas (the lazy WheelPreview).

const WheelPreview = React.lazy(() => import(/* webpackChunkName: "wheel-preview" */ './WheelPreview'));

function useFullscreen(ref) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(!!document.fullscreenElement && document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [ref]);
  const can = typeof document !== 'undefined' && !!document.fullscreenEnabled;
  const toggle = () => {
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    else ref.current?.requestFullscreen?.().catch(() => {});
  };
  return { on, can, toggle };
}

/**
 *   wheel      normalised PrizeWheel (liveSpin, rounds, participantCount …)
 *   serverNow  () => server ms (stable)
 *   ready      { remainingMs, onStart, starting, error, canStart } while waiting for the brand, else null
 */
export default function WheelLiveStage({ wheel, brand, serverNow, ready = null }) {
  const { t, lang } = useLocale();
  const stageRef = useRef(null);
  const fs = useFullscreen(stageRef);
  useNow(250);

  const spin = wheel.liveSpin;
  const spinKey = spin ? `${spin.startAt}:${spin.rounds.map((r) => r.winnerIndex).join(',')}:${spin.participantCount}` : 'idle';
  const plan = useMemo(() => planSpin(spin), [spinKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const live = useMemo(() => ({ spin, now: serverNow }), [spinKey, serverNow]); // eslint-disable-line react-hooks/exhaustive-deps
  const mo = momentAt(plan, serverNow());
  const total = plan.length || wheel.winnerCount;

  const shown = mo.round && (mo.phase === 'reveal' || mo.phase === 'done') ? mo.round.round : null;
  const winnerRaw = shown ? spin?.rounds[shown - 1]?.winner : null;
  const winner = shown
    ? normalizeWheelWinner(winnerRaw) || (wheel.winners || []).find((w) => w.round === shown) || null
    : null;
  const prizes = shown ? wheel.rounds.find((r) => r.round === shown)?.products ?? [] : [];

  let label;
  if (ready) label = t('wheel.live.ready');
  else if (mo.phase === 'armed') label = t('wheel.live.startsIn', { s: Math.ceil(mo.untilStartMs / 1000) });
  else if (mo.phase === 'spinning') label = t('wheel.live.spinning', { n: mo.round.round, total });
  else if (mo.phase === 'reveal') label = t('wheel.live.roundWinner', { n: mo.round.round, total });
  else if (mo.phase === 'done') label = t('wheel.live.done');
  else label = t(`wheel.status.${wheel.status}`);

  const points = Math.min(50000, spin?.participantCount ?? wheel.participantCount ?? 0);

  return (
    <div ref={stageRef} className={`wheel-stage${fs.on ? ' is-fullscreen' : ''}`}>
      <Suspense fallback={<div className="wheel-viewer wheel-viewer--stage wheel-viewer--fallback" />}>
        <WheelPreview
          className="wheel-viewer--stage"
          logoUrl={wheel.brand?.logoUrl || brand.logo || null}
          brandColor={wheel.brand?.brandColor ?? brand.brandColor ?? null}
          brandName={brand.name}
          points={points}
          count={wheel.participantCount}
          live={live}
          demo={false}
        />
      </Suspense>

      <div className="wheel-stage__top">
        <div className="wheel-stage__chips">
          <span className="wheel-chip wheel-stage__title">{wheel.title}</span>
          <span className={`wheel-chip wheel-stage__phase is-${ready ? 'ready' : mo.phase}`} role="status">{label}</span>
          <span className="wheel-chip wheel-chip--muted"><PeopleFill size={11} aria-hidden="true" /> {fmtNum(wheel.participantCount, lang)}</span>
        </div>
        {fs.can && (
          <button type="button" className="wheel-stage__fs" onClick={fs.toggle}>
            {fs.on ? <FullscreenExit size={14} /> : <Fullscreen size={14} />}
            <span>{t(fs.on ? 'wheel.live.exitFullscreen' : 'wheel.live.fullscreen')}</span>
          </button>
        )}
      </div>

      {ready && (
        <div className="wheel-stage__start">
          {ready.canStart ? (
            <button type="button" className="wheel-stage__go" onClick={ready.onStart} disabled={ready.starting}>
              <PlayFill size={22} aria-hidden="true" />
              {ready.starting ? t('wheel.live.starting') : t('wheel.live.start')}
            </button>
          ) : (
            <div className="wheel-stage__waiting">{t('wheel.live.waiting')}</div>
          )}
          <div className="wheel-stage__auto">
            {t('wheel.live.auto', { s: Math.max(0, Math.ceil(ready.remainingMs / 1000)) })}
          </div>
          {ready.error && <div className="wheel-stage__error" role="alert">{ready.error}</div>}
        </div>
      )}

      {shown && (
        <div className="wheel-stage__reveal" aria-live="polite">
          <div className="wheel-stage__reveal-round">{t('wheel.live.roundWinner', { n: shown, total })}</div>
          <div className="wheel-stage__reveal-who">
            <Avatar winner={winner} size={fs.on ? 56 : 40} />
            <span className="wheel-stage__reveal-name">{winner && !winner.anonymous && winner.name ? winner.name : t('wheel.someone')}</span>
          </div>
          {prizes.length > 0 && (
            <div className="wheel-stage__reveal-prizes">
              {prizes.slice(0, 4).map((p) => <ProductThumb key={p.id} product={p} size={fs.on ? 52 : 36} />)}
              <span className="wheel-stage__reveal-names">{prizes.map((p) => p.name).join(', ')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
